/**
 * Sales Closures Calculator Utility
 * Handles all business logic for calculating commissions, taxes, and suggested amounts
 *
 * @module utils/salesClosuresCalculator
 */

/**
 * Calculate commission amount based on base amount and percentage
 * @param {number} baseAmount - Base amount for calculation
 * @param {number} percentage - Commission percentage
 * @returns {number} Calculated commission amount
 */
export const calculateCommissionAmount = (baseAmount, percentage) => {
  if (!baseAmount || !percentage) return 0
  return (baseAmount * percentage) / 100
}

/**
 * Calculate all commissions and taxes for a closure
 * @param {Object} closure - Closure data object
 * @returns {Object} Calculated commissions and taxes
 */
export const calculateClosureAmounts = (closure) => {
  const amountBase = closure.amount || 0
  const calculationBase = closure.calculation_base !== undefined && closure.calculation_base !== null
    ? closure.calculation_base
    : amountBase

  const agencyCommission = calculateCommissionAmount(amountBase, closure.agency_commission_percentage)
  const exchangeCommission = calculateCommissionAmount(amountBase, closure.exchange_commission_percentage)

  // The WHT adjustment runs on its own base, never on the agency calculation base
  const internationalTax = calculateCommissionAmount(
    closure.wht_calculation_base,
    closure.international_tax_percentage
  )

  return {
    agencyCommission: parseFloat(agencyCommission.toFixed(2)),
    exchangeCommission: parseFloat(exchangeCommission.toFixed(2)),
    internationalTax: parseFloat(internationalTax.toFixed(2)),
    calculationBase: calculationBase
  }
}

const toNumber = (value) => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Net amount: media owner payment plus both commissions
 * Fallback only - the backend returns net_amount and it is the source of truth
 * @param {Object} closure - Closure data object
 * @returns {number} Net amount
 */
export const calculateNetAmount = (closure) => {
  const netAmount =
    toNumber(closure.amount)
  return parseFloat(netAmount.toFixed(2))
}

/**
 * Billing amount: net amount plus the national tax and the WHT adjustment.
 * Mirrors the backend formula (net_amount + national_tax + WHT) so the form preview matches
 * the billing_amount that Facturación and Cobro read straight from the API.
 * @param {Object} closure - Closure data object
 * @returns {number} Billing amount
 */
export const calculateBillingAmount = (closure) => {
  const billingAmount =
    calculateNetAmount(closure) +
    toNumber(closure.national_tax_amount) +
    toNumber(closure.international_tax_amount)

  return parseFloat(billingAmount.toFixed(2))
}

const parseIsoDateAsLocal = (value) => {
  if (!value) return new Date(value)

  const normalized = String(value).trim().split('T')[0]
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/)

  if (!match) {
    return new Date(value)
  }

  const year = Number(match[1])
  const monthIndex = Number(match[2]) - 1
  const day = Number(match[3])

  return new Date(year, monthIndex, day)
}

/**
 * Cap the consumption at the purchase order amount: MIN(ODC, consumption)
 * Only used by the local fallback - the backend caps the amount it returns
 * @param {number} consumption - Consumption amount
 * @param {number} odcAmount - Total of the campaign purchase orders, in the campaign currency
 * @returns {number} The lower of both, or the consumption when there is no ODC loaded
 */
export const capConsumptionToOdc = (consumption, odcAmount) => {
  const consumptionValue = parseFloat(consumption) || 0
  const odcValue = parseFloat(odcAmount) || 0

  if (odcValue <= 0) return consumptionValue

  return Math.min(consumptionValue, odcValue)
}

/**
 * Calculate suggested amount based on billing mode and consumption data
 * @param {string} billingMode - Billing mode from BILLING_MODES
 * @param {string} paymentMode - Payment mode from PAYMENT_MODES
 * @param {Object} dateRange - Object with dateFrom and dateTo
 * @param {Object} campaignData - Campaign data containing budget and consumption
 * @returns {number} Suggested amount
 */
export const calculateSuggestedAmount = (billingMode, paymentMode, dateRange, campaignData) => {
  // If payment is by full ODC, suggest budget; otherwise suggest consumption capped at the ODC,
  // so a closure never exceeds the purchase order amount
  const isFullODC = paymentMode === 'always_pay_full_po' || paymentMode === 'pay_full_odc'
  const dataSource = isFullODC
    ? campaignData.budgetTotal
    : capConsumptionToOdc(campaignData.consumptionReal, campaignData.odcAmount)

  switch (billingMode) {
    case 'full_campaign_start_100':
    case 'full_campaign_start':
      // Full campaign budget/consumption
      return dataSource || 0

    case 'campaign_start_partial_months':
    case 'monthly_partial_start':
      // Monthly budget/consumption for the selected month
      return filterByDateRange(dataSource, dateRange) || 0

    case 'campaign_completed_no_monthly_break':
    case 'final_campaign_no_monthly_cut':
      // Total campaign consumption at the end
      return dataSource || 0

    case 'monthly_closure':
      // Monthly consumption for the selected month
      return filterByDateRange(dataSource, dateRange) || 0

    default:
      return 0
  }
}

/**
 * Filter data by date range - placeholder for backend-driven calculation
 * @param {number} data - Data value
 * @param {Object} dateRange - Date range filter
 * @returns {number} Filtered sum
 */
const filterByDateRange = (data, dateRange) => {
  if (!data || !dateRange) return 0
  // This would filter the data based on dateFrom and dateTo
  // Implementation depends on data structure from backend
  return data || 0
}

export const OVERLAP_ERROR_MESSAGE = 'Las fechas del cierre se superponen con otro cierre existente'

/**
 * Validate that closures do not overlap in dates
 * @param {Array} existingClosures - Array of existing closures
 * @param {Object} newClosure - New closure to validate
 * @param {string|number} excludeClosureId - Closure ID to exclude from check (for edit scenarios)
 * @returns {Object} Validation result with isValid and message
 */
export const validateNoDateOverlap = (existingClosures = [], newClosure, excludeClosureId = null) => {
  const activeClosures = existingClosures.filter(
    closure => closure.status !== 'canceled' && closure.id !== excludeClosureId
  )

  const hasOverlap = activeClosures.some(closure => {
    const newFrom = parseIsoDateAsLocal(newClosure.date_from)
    const newTo = parseIsoDateAsLocal(newClosure.date_to)
    const existingFrom = parseIsoDateAsLocal(closure.date_from)
    const existingTo = parseIsoDateAsLocal(closure.date_to)

    // Check if ranges overlap
    return newFrom <= existingTo && newTo >= existingFrom
  })

  return {
    isValid: !hasOverlap,
    message: hasOverlap ? OVERLAP_ERROR_MESSAGE : ''
  }
}

/**
 * Format closure data for API submission
 * @param {Object} closureFormData - Raw form data
 * @returns {Object} Formatted closure data for API
 */
export const formatClosureForSubmit = (closureFormData) => {
  const calculations = calculateClosureAmounts(closureFormData)

  return {
    amount: parseFloat(closureFormData.amount),
    currency: closureFormData.currency,
    date_from: closureFormData.date_from,
    date_to: closureFormData.date_to,
    billing_information_id: closureFormData.billing_information_id,
    billing_mode: closureFormData.billing_mode,
    payment_mode: closureFormData.payment_mode,
    payment_term: parseInt(closureFormData.payment_term),
    charge_term: parseInt(closureFormData.charge_term),
    agency_commission_mode: closureFormData.agency_commission_mode,
    agency_commission_percentage: parseFloat(closureFormData.agency_commission_percentage),
    agency_commission_currency: closureFormData.agency_commission_currency,
    // FIXME: recalculated from amount * percentage, so an amount the user typed by hand in the
    // form is silently discarded on save. The WHT and national tax amounts below are sent as-is
    // for that reason; these two should follow, but it changes behaviour outside this ticket.
    agency_commission_amount: calculations.agencyCommission,
    exchange_commission_percentage: parseFloat(closureFormData.exchange_commission_percentage),
    exchange_commission_currency: closureFormData.exchange_commission_currency,
    // FIXME: same as agency_commission_amount above
    exchange_commission_amount: calculations.exchangeCommission,
    international_tax_percentage: parseFloat(closureFormData.international_tax_percentage) || 0,
    // Shared by the WHT base and amount: the backend requires both to be in the same currency
    international_tax_currency: closureFormData.international_tax_currency,
    // Sent as-is so a manually edited amount is persisted instead of being recalculated away
    international_tax_amount: parseFloat(closureFormData.international_tax_amount) || 0,
    wht_calculation_base: parseFloat(closureFormData.wht_calculation_base) || 0,
    national_tax_percentage: parseFloat(closureFormData.national_tax_percentage),
    national_tax_currency: closureFormData.national_tax_currency,
    // Sent as-is so a manually edited amount is persisted instead of being recalculated away
    national_tax_amount: parseFloat(closureFormData.national_tax_amount) || 0,
    calculation_base: calculations.calculationBase,
    closure_observation: closureFormData.closure_observation || null,
    billing_comment: closureFormData.billing_comment || null,
    status: closureFormData.status || 'draft',
    company_fantasy_name: closureFormData.company_fantasy_name,
    company_legal_name: closureFormData.company_legal_name
  }
}

/**
 * Get multiple currency totals for display
 * @param {Array} closures - Array of closures
 * @returns {Object} Totals by currency with USD conversion if needed
 */
export const getMultiCurrencyTotals = (closures = []) => {
  const totals = {}
  const currencies = new Set()
  let isMultiCurrency = false

  closures.forEach(closure => {
    if (closure.status === 'canceled') return

    const currency = closure.currency || 'USD'
    currencies.add(currency)
  })

  // Determine if multi-currency
  isMultiCurrency = currencies.size > 1

  // Build totals
  closures.forEach(closure => {
    if (closure.status === 'canceled') return

    const currency = closure.currency || 'USD'

    if (!totals[currency]) {
      totals[currency] = 0
    }

    // Use amount_usd if available and multi-currency, otherwise use amount
    const amount = isMultiCurrency && closure.amount_usd !== undefined ? closure.amount_usd : (closure.amount || 0)
    totals[currency] += amount
  })

  // If multi-currency, also add USD total
  if (isMultiCurrency) {
    let usdTotal = 0
    closures.forEach(closure => {
      if (closure.status === 'canceled') return
      usdTotal += closure.amount_usd || 0
    })
    totals['USD'] = usdTotal
    currencies.add('USD')
  }

  return {
    byCurrency: Object.fromEntries(
      Array.from(currencies).map(currency => [currency, totals[currency]])
    ),
    isMultiCurrency: isMultiCurrency,
    currencies: Array.from(currencies).sort()
  }
}

/**
 * Generate suggested date range based on billing mode
 * @param {string} billingMode - Billing mode
 * @param {Object} campaignDates - Campaign dates
 * @param {Array} existingClosures - Existing closures to find gaps
 * @returns {Object} Suggested date range
 */
export const generateSuggestedDateRange = (billingMode, campaignDates, existingClosures = []) => {
  const { start_date: campaignStart, end_date: campaignEnd } = campaignDates

  switch (billingMode) {
    case 'full_campaign_start_100':
    case 'full_campaign_start':
      return {
        dateFrom: campaignStart,
        dateTo: campaignEnd
      }

    case 'campaign_start_partial_months':
    case 'monthly_partial_start':
    case 'monthly_closure':
      // Find the last month without a closure
      return findLastMonthGap(campaignStart, campaignEnd, existingClosures)

    case 'campaign_completed_no_monthly_break':
    case 'final_campaign_no_monthly_cut':
      return {
        dateFrom: campaignStart,
        dateTo: campaignEnd
      }

    default:
      return { dateFrom: null, dateTo: null }
  }
}

/**
 * Find the last month gap without an active closure
 * @param {string} startDate - Campaign start date
 * @param {string} endDate - Campaign end date
 * @param {Array} existingClosures - Existing closures
 * @returns {Object} Date range for the last unclosed month
 */
const findLastMonthGap = (startDate, endDate, existingClosures) => {
  const start = parseIsoDateAsLocal(startDate)
  const end = parseIsoDateAsLocal(endDate)

  const activeClosures = existingClosures.filter(c => c.status !== 'canceled')

  const formatDateLocal = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  if (activeClosures.length === 0) {
    const firstMonthStart = new Date(start)
    firstMonthStart.setDate(1)

    const firstMonthEnd = new Date(firstMonthStart.getFullYear(), firstMonthStart.getMonth() + 1, 0)
    const dateTo = firstMonthEnd > end ? end : firstMonthEnd

    return {
      dateFrom: formatDateLocal(start),
      dateTo: formatDateLocal(dateTo)
    }
  }

  // Start from the end and work backwards to find last gap
  let currentDate = new Date(end)
  currentDate.setDate(1) // Start from the first of the month

  // Find first uncovered month
  while (currentDate >= start) {
    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)

    const isCovered = activeClosures.some(closure => {
      const closureFrom = parseIsoDateAsLocal(closure.date_from)
      const closureTo = parseIsoDateAsLocal(closure.date_to)
      return closureFrom <= monthEnd && closureTo >= currentDate
    })

    if (!isCovered) {
      return {
        dateFrom: formatDateLocal(currentDate),
        dateTo: formatDateLocal(monthEnd)
      }
    }

    // Move to previous month
    currentDate.setMonth(currentDate.getMonth() - 1)
  }

  return { dateFrom: null, dateTo: null }
}
